import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./index.less";
import { IServiceVideoChatAvatar } from "@obsbot/remo-services";
import {
    InterActionMessageType,
    ShowAnimationType,
    SpecialEffectTo,
    useChannelRoomStore,
    useChannelStore,
    useGlobalStore,
    usePreferencesStore,
    User,
    useUserStore,
    VideoWindowLayoutTypes,
} from "@obsbot/remo-stores";
import { SpecialEffectCanvas } from "../ChannelsCom/SpecialEffectCanvas";
import { eventBus, RGBACanvas3DRenderer } from "@obsbot/remo-utils";
import { SpecialEffectIconUsed } from "../ChannelsCom/SpecialEffect";
import {
    SoundBoomMP3,
    SoundCelebrateMP3,
    SoundCryMP3,
    SoundFaceChangeMP3,
    SoundHeartMP3,
    SoundSkateMP3,
    SoundTornadoMP3,
    SoundWelcomeMP3,
} from "../SvgIconCom";
import { throttle } from "lodash-es";
import Lottie from "lottie-web";
import { VideoWindow } from "./VideoWindow";
import { deepClone, SpecialEffectRenderDo } from "../../utils/LottieAnimationHandler";
export interface UserVideoType {
    user: User;
    rtcAvatar?: IServiceVideoChatAvatar | null;
}
export interface VideoWindowLayoutProps {}

export const SOUND_MAP: Record<SpecialEffectIconUsed, any> = {
    FaceChangeIcon: SoundFaceChangeMP3,
    TornadoIcon: SoundTornadoMP3,
    WelcomeIcon: SoundWelcomeMP3,
    CryIcon: SoundCryMP3,
    EmptyIcon: undefined,
    HeartIcon: SoundHeartMP3,
    BombIcon: SoundBoomMP3,
    SkateIcon: SoundSkateMP3,
    CelebrateIcon: SoundCelebrateMP3,
};

interface SpecialEffectQueue {
    [uid: string]: InterActionMessageType[];
}

export const VideoWindowLayout: React.FC<VideoWindowLayoutProps> = () => {
    const globalStore = useGlobalStore();
    const preferenceStore = usePreferencesStore(globalStore.userInfo?.user_id ?? "")?.(
        state => state,
    );
    const { rtc, users, channelSpecialList, updateSpecialList, appendAudioList, removeAudioList } =
        useChannelRoomStore();
    const [members, setMembers] = useState<UserVideoType[]>([]);
    const layoutClass =
        members?.length >= 8 ? "most-items" : members?.length >= 4 ? "middle-items" : "least-items";
    const [mainContent, setMainContent] = useState<UserVideoType | null>(members && members?.[0]);
    const videoRefs = useRef<Map<string, HTMLElement | null>>(new Map());
    const canvasSpecialRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const { currentUser, cacheUser } = useUserStore();
    const { userState } = useChannelStore();
    const isSetRenderer = useRef<Map<string, boolean>>(new Map());
    // 追踪每个用户是否正在播放特效
    const isAnimating = useRef<Map<string, boolean>>(new Map());
    // 维护每个用户的特效队列
    const effectQueues = useRef<SpecialEffectQueue>({});
    // 追踪已处理的特效ID，防止重复处理
    const handledEffectIds = useRef(new Set<string>());
    // 添加：追踪元素是否在视口中
    const isInViewportRef = useRef<Map<string, boolean>>(new Map());
    // 添加：IntersectionObserver 实例
    const observersRef = useRef<Map<string, IntersectionObserver>>(new Map());
    // 是否是拖拽的目标
    const [draggingTarget, setDraggingTarget] = useState<string>("");
    // 是否正在拖拽
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const rgbRenderers = useRef<Map<string, RGBACanvas3DRenderer>>(new Map());

    // 处理是否需要显示特效互动
    const isShowSpecialIcon = useMemo(() => {
        if (channelSpecialList) {
            return true;
        }
        return false;
    }, [channelSpecialList]);

    const processNextEffect = useCallback(
        (uid: string) => {
            const queue = effectQueues.current[uid];
            // 队列不存在、为空或当前正在播放则返回
            if (!queue || queue.length === 0 || isAnimating.current.get(uid)) {
                if (queue && queue.length === 0) {
                    // 如果队列为空，清理该 uid 对应的队列
                    delete effectQueues.current[uid];
                }
                return;
            }
            // 添加：检查元素是否在视口中
            if (!isInViewportRef.current.get(uid)) {
                return; // 不在视口中，跳过渲染
            }
            // 从队列头部取出并移除，确保是按序的
            const nextEffect = queue.shift();
            const canvas = canvasSpecialRefs.current.get(uid);
            if (canvas && nextEffect) {
                doRenderSpecial(canvas, uid, nextEffect);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canvasSpecialRefs],
    );

    const doRenderSpecial = useCallback(
        (
            element: HTMLDivElement,
            uid: string,
            currentSpecialEffect: InterActionMessageType,
        ): void => {
            if (isAnimating.current.get(uid)) {
                return;
            }
            // 将当前uid设置为true
            isAnimating.current.set(uid, true);
            appendAudioList({
                soundEffect: currentSpecialEffect.soundEffect,
                actionID: currentSpecialEffect.actionID,
            });
            const animationData = deepClone(currentSpecialEffect.icon);
            const animation = Lottie.loadAnimation({
                container: element,
                renderer: "canvas",
                loop: false,
                autoplay: true,
                animationData,
            });
            animation.setSubframe(false);
            // 处理额外的Lottie播放逻辑
            const extraDoing =
                SpecialEffectRenderDo[currentSpecialEffect.soundEffect as SpecialEffectIconUsed];
            extraDoing?.(animation);

            animation.addEventListener("DOMLoaded", () => {
                element.classList.add("fade-in");
                const handleTransitionEnd = (): void => {
                    element.classList.remove("fade-in");
                    element.removeEventListener("transitionend", handleTransitionEnd);
                };
                element.addEventListener("transitionend", handleTransitionEnd);
            });

            animation.addEventListener("complete", () => {
                element.classList.add("fade-out");
                // 2. 监听 CSS 过渡完成事件
                // 'transitionend' 事件会在 CSS transition 完成时触发
                const handleTransitionEnd = (): void => {
                    removeAudioList({
                        soundEffect: currentSpecialEffect.soundEffect,
                        actionID: currentSpecialEffect.actionID,
                    });
                    // 3. 销毁 Lottie 实例
                    animation.destroy();
                    element.classList.remove("fade-out");
                    // 5. 移除事件监听器，避免重复执行
                    element.removeEventListener("transitionend", handleTransitionEnd);
                    isAnimating.current.set(uid, false);
                    const { channelSpecialList: latestList, updateSpecialList: latestUpdate } =
                        useChannelRoomStore.getState();
                    const newList = latestList.filter(
                        item => item.actionID !== currentSpecialEffect.actionID,
                    );
                    latestUpdate(newList);
                    handledEffectIds.current.delete(currentSpecialEffect.actionID);
                    processNextEffect(uid);
                };
                element.addEventListener("transitionend", handleTransitionEnd);
                // animation.destroy();
            });
        },
        [processNextEffect, appendAudioList, removeAudioList],
    );

    useEffect(() => {
        if (
            preferenceStore?.showAnimationType === ShowAnimationType.Default &&
            channelSpecialList.length
        ) {
            const specialEffects: InterActionMessageType[] = JSON.parse(
                JSON.stringify(channelSpecialList),
            );
            const newSpecialEffects = specialEffects.filter(
                effect => !handledEffectIds.current.has(effect.actionID),
            );
            newSpecialEffects.forEach(effect => {
                let uidToQueue: string[];
                try {
                    const uid = JSON.parse(effect.toType).userID;
                    uidToQueue =
                        effect.toType === SpecialEffectTo.All
                            ? Array.from(canvasSpecialRefs.current.keys())
                            : [uid];
                } catch (error) {
                    uidToQueue =
                        effect.toType === SpecialEffectTo.All
                            ? Array.from(canvasSpecialRefs.current.keys())
                            : [effect.to];
                }

                uidToQueue.forEach(uid => {
                    (effectQueues.current[uid] ??= []).push(effect);
                    processNextEffect(uid);
                });

                newSpecialEffects.forEach(effect => handledEffectIds.current.add(effect.actionID));
            });
        }
    }, [
        preferenceStore?.showAnimationType,
        doRenderSpecial,
        channelSpecialList,
        processNextEffect,
    ]);

    // 设置一下播放音频音量
    useEffect(() => {
        if ((rtc as any)?.enabledListen !== undefined) {
            (rtc as any).enabledListen = userState.isListen;
        }
        rtc?.setIsMuteAllRemoteAudio(!userState.isListen);
    }, [rtc, userState.isListen]);

    // 处理拖动特效到用户
    useEffect(() => {
        const handleDragSpecialEffect = ({
            to,
            isStarted,
        }: {
            to: string;
            isStarted: boolean;
        }): void => {
            const canvasEl = canvasSpecialRefs.current.get(to);
            setIsDragging(isStarted);
            if (canvasEl) {
                const parent = canvasEl.parentElement;
                if (!parent) {
                    return;
                }
                // 代表在拖拽特效
                setDraggingTarget(isStarted ? to : "");
            }
        };
        const handleWindowDragEnd = (): void => {
            setIsDragging(false);
            setDraggingTarget("");
        };
        window.addEventListener("dragend", handleWindowDragEnd);
        eventBus.on("drag-special-effect", handleDragSpecialEffect);
        const handleSwitchChannel = (): void => {
            isAnimating.current.clear();
            updateSpecialList([]);
        };
        eventBus.on("switch-channel", handleSwitchChannel);
        return () => {
            eventBus.off("drag-special-effect", handleDragSpecialEffect);
            eventBus.off("switch-channel", handleSwitchChannel);
            window.addEventListener("dragend", handleWindowDragEnd);
        };
    }, []);

    useEffect(() => {
        const tempMembers: UserVideoType[] = [];
        const cachedUsers = users?.cachedUsers ?? new Map();
        cachedUsers.forEach(user => {
            const curRtcAvatar = user.userUUID ? rtc?.getAvatar(user.userUUID) : null;
            const element = videoRefs.current.get(user.userUUID);

            if (curRtcAvatar) {
                const isCurrentUser = user.userUUID === userState?.userUUID;
                const camera = isCurrentUser ? Boolean(userState.camera) : Boolean(user.camera);
                const mic = isCurrentUser ? Boolean(userState.mic) : Boolean(user.mic);
                isSetRenderer.current.set(user.userUUID, camera);
                if (isCurrentUser && (user.camera !== camera || user.mic !== mic)) {
                    user.camera = camera;
                    user.mic = mic;
                    cacheUser({ ...user });
                }
                setView(camera, mic, curRtcAvatar, element, user.userUUID);
            }
            tempMembers.push({
                user,
                rtcAvatar: curRtcAvatar,
            });
        });
        setMembers(tempMembers);

        return () => {
            // 清理视图
            users?.cachedUsers.forEach(m => {
                const curRtcAvatar = m.userUUID ? rtc?.getAvatar(m.userUUID) : null;
                setView(false, false, curRtcAvatar, null);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users?.cachedUsers, rtc]);

    useEffect(() => {
        const handleRefreshAvatar = (): void => {
            const avatar = rtc?.getAvatar("");
            if (avatar) {
                avatar.refreshAvatar();
            }
        };
        eventBus.on("refresh-local-avatar", handleRefreshAvatar);
        return () => eventBus.off("refresh-local-avatar", handleRefreshAvatar);
    }, [rtc]);

    useEffect(() => {
        const channelLayoutChanged = (): void => {
            setImmediate(() => {
                const rtcAvatar = members.filter(member => member.user.camera);
                rtcAvatar.forEach(user => {
                    const uid = user.user.userUUID;
                    const el = videoRefs.current.get(uid);
                    user.rtcAvatar?.updateElementView(el || null);
                });
            });
        };
        eventBus.on("channel-layout-type-changed", channelLayoutChanged);
        return () => eventBus.off("channel-layout-type-changed", channelLayoutChanged);
    }, [currentUser, members, mainContent]);

    // 在组件内部定义setView函数
    const setView = async (
        camera: boolean,
        mic: boolean,
        rtcAvatar?: IServiceVideoChatAvatar | null,
        view?: HTMLElement | null,
        uid?: string,
    ): Promise<void> => {
        if (!camera && uid) {
            rgbRenderers.current.delete(uid);
        }
        if (rtcAvatar && view) {
            rtcAvatar.updateElementView(view ?? null);
            rtcAvatar.enableCamera(camera);
            rtcAvatar.enableMic(mic);
            if (uid !== currentUser?.userUUID && camera && uid) {
                const renderer = rgbRenderers.current.get(uid);
                const hasVideo = renderer && renderer.hasContent();
                if (!hasVideo) {
                    rgbRenderers.current.delete(uid);
                    rtcAvatar.refreshAvatar();
                }
            }
        }
    };

    const setRefs =
        (userID: string, videoRef: HTMLDivElement) =>
        (refs: { divRef: HTMLDivElement }): void => {
            if (refs) {
                const avatar = rtc?.getAvatar(userID);
                canvasSpecialRefs.current.set(userID, refs.divRef);
                videoRefs.current.set(userID, videoRef);
                avatar?.updateElementView(videoRef);

                // 清理现有的观察器（如果存在）
                const existingObserver = observersRef.current.get(userID);
                if (existingObserver) {
                    existingObserver.disconnect();
                    observersRef.current.delete(userID);
                }

                // 初始化可见性状态
                isInViewportRef.current.set(userID, false);

                // 创建新的 IntersectionObserver
                const observer = new IntersectionObserver(
                    ([entry]) => {
                        isInViewportRef.current.set(userID, entry.isIntersecting);
                        // 当元素可见时尝试处理下一个特效
                        if (entry.isIntersecting) {
                            processNextEffect(userID);
                        }
                    },
                    { threshold: 0.1 }, // 当10%元素可见时触发
                );

                if (refs.divRef) {
                    observer.observe(refs.divRef);
                    observersRef.current.set(userID, observer);
                }
            }
        };

    const getVideoItemClassName = useCallback(
        (member: UserVideoType): string => {
            let className = `video-window-item video-window-item-${member.user.userUUID}`;
            if (isDragging && draggingTarget === member.user.userUUID) {
                className += " dragging-special-effect";
            }
            if (member.user.isSpeak && member.user.mic) {
                className += " open-microphone";
            }
            if (mainContent?.user.userUUID === member.user.userUUID) {
                className += " main-content";
            }
            if (currentUser?.userUUID === member.user.userUUID) {
                className += " self-content";
            }
            return className;
        },
        [currentUser?.userUUID, mainContent?.user.userUUID, isDragging, draggingTarget],
    );

    useEffect(() => {
        const handleResize = throttle((): void => {
            setMembers(prev => [...prev]);
        }, 100);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (!rtc) {
            return;
        }
        const handleVideoStreamData = async ({
            uid,
            data,
        }: {
            uid: string;
            data: { width: number; height: number; data: Uint8Array };
        }): Promise<void> => {
            const videoContainer = videoRefs.current.get(uid) as HTMLDivElement;
            if (!videoContainer) {
                return;
            }
            // 提高转发到Overlay的位置, 防止出了窗口后，监听不到, 不会转发
            if (uid !== useGlobalStore.getState().userInfo?.user_id) {
                const canvas = videoContainer.querySelector("canvas") as HTMLCanvasElement;
                let renderer = rgbRenderers.current.get(uid);
                if (!canvas) {
                    return;
                }
                if (canvas && !canvas.getAttribute("width")) {
                    rgbRenderers.current.delete(uid);
                }
                if (!renderer) {
                    renderer = new RGBACanvas3DRenderer(canvas);
                    rgbRenderers.current.set(uid, renderer);
                }
                renderer.draw(data.data, data.width, data.height);
            }
        };
        rtc.events.on("video-stream-data", handleVideoStreamData);
        return () => {
            rtc.events.off("video-stream-data", handleVideoStreamData);
        };
    }, [rtc]);

    useEffect(() => {
        return () => {
            // 清理所有 IntersectionObserver
            observersRef.current.forEach(observer => {
                observer.disconnect();
            });
            observersRef.current.clear();
        };
    }, []);

    return (
        <div
            className={`video-window-layout ${layoutClass} ${preferenceStore?.channelLayoutType} ${preferenceStore?.channelLayoutType === VideoWindowLayoutTypes.Equal && members.length > 1 ? (members.length <= 4 ? "equal-normal" : "equal-many") : ""} ${globalStore.needShowGuide.channel || globalStore.needShowGuide.specialEffect ? "guiding" : ""}`}
            onDragOver={e => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
            }}
        >
            {preferenceStore?.showAnimationType === ShowAnimationType.Fullscreen &&
                channelSpecialList && (
                    <SpecialEffectCanvas
                        isShowSpecialIcon={isShowSpecialIcon}
                        renderIcons={channelSpecialList}
                        setRenderIcons={updateSpecialList}
                    />
                )}
            <VideoWindow
                channelLayoutType={
                    preferenceStore?.channelLayoutType ?? VideoWindowLayoutTypes.Equal
                }
                genClassName={getVideoItemClassName}
                mainContent={mainContent}
                members={members}
                setMainContent={setMainContent}
                setRefs={setRefs}
            />
        </div>
    );
};
